import { keyBy, map, uniqBy } from 'lodash';
import moment from 'moment';
import errorExtractor from '../../shared/Utils/ErrorExtractor';
import { APP_EVENTS, DOCUMENT_STATUSES, DOCUMENT_STATUS_LIST } from '../Constants/Constants';
import { publish } from '../Events/AppEvents';
import { getDecimalValue } from './Utils';

const groupBy = (xs, key) => {
  return xs.reduce((rv, x) => {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});
};

function numberWithCommas(x) {
  if (!x) return x;
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

const getDocumentByType = (documents, type) => {
  return documents.filter(({ document }) => document?.type === type);
};

const getColorByDocType = (documents, type) => {
  let { color } = DOCUMENT_STATUS_LIST.find((ds) => ds.id === DOCUMENT_STATUSES.NO_DOC) || {};

  if (documents?.length > 0) {
    const delDocuments = getDocumentByType(documents, type);
    let status = DOCUMENT_STATUSES.NO_DOC;
    delDocuments.forEach(({ status: docStatus }) => {
      if (docStatus === DOCUMENT_STATUSES.APPROVED) status = docStatus;
      else if (docStatus === DOCUMENT_STATUSES.IN_REVIEW) status = docStatus;
      else status = docStatus;
    });
    const { color: newColor } = DOCUMENT_STATUS_LIST.find((ds) => ds.id === status) || {};
    color = newColor || color;
  }
  return color;
};

const toLocalDate = (dateString, format = 'DD-MMM-YYYY') => {
  if (!dateString) return '';
  return moment(dateString).format(format);
};

const toLocalDisplayDate = (dateString, format = 'MMMM YYYY') => {
  if (!dateString) return '';
  return moment(dateString).format(format);
};

export const toFinYear = (dateString1, dateString2) => {
  if (!dateString1 || !dateString2) return '';
  return `${new Date(dateString1).getFullYear()}-${new Date(dateString2).getFullYear()}`;
};

export const toFinYearShort = (dateString1, dateString2) => {
  if (!dateString1 || !dateString2) return '';
  return `${new Date(dateString1).getFullYear()}-${`${new Date(dateString2).getFullYear()}`?.slice(2)}`;
};

const toLocalDateTime = (dateString, format = 'DD-MMM-YYYY . hh:mm A') => {
  if (!dateString) return '';
  return moment(dateString).format(format);
};

export const dateToISOString = (dateStr) => {
  const date = moment(dateStr);
  return date.toISOString();
};

const toServerDate = (date, onlyDate, format = 'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]') => {
  const _date = moment(date);
  return onlyDate ? _date.startOf('day').utc().format(format) : _date.utc().format(format);
};

const toServerFromAndToDate = (range, format = 'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]') => {
  const _startDate = moment(range[0]);
  const _endDate = moment(range[1]);
  const dateRange = {
    startDate: _startDate.startOf('day').utc().format(format),
    endDate: _endDate.endOf('day').utc().format(format),
  };
  return dateRange;
};

function toCapitalize(str) {
  if (!str || typeof str !== 'string') return str;
  return str
    ?.split('_')
    .map((frag) => frag.charAt(0).toUpperCase() + frag.slice(1).toLowerCase())
    .join(' ');
}

export const toTitleCase = (str) =>
  str
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : ''))
    .join(' ');

const parseQueryParams = (queryString) => {
  const queryParams = {};
  const query = new URLSearchParams(queryString);
  for (const [key, value] of query.entries()) {
    if (queryParams[key]) {
      const existingParamsArr =
        typeof queryParams[key] === 'object' ? queryParams[key] : [queryParams[key]];
      queryParams[key] = [...existingParamsArr, value];
    } else {
      queryParams[key] = value;
    }
  }
  return queryParams;
};

// utility function at the top of the file after imports
const buildQueryString = (params) => {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });

  return queryParams.toString();
};

const getCurrentYearStartEndDates = (selectedFinancialYear, month) => {
  return {
    fromDate: moment
      .utc(moment(selectedFinancialYear?.fromDate).month(month).startOf('month'))
      .format(),
    toDate: moment
      .utc(moment(selectedFinancialYear?.fromDate).month(month).endOf('month'))
      .format(),
  };
};

const getNextYearStartEndDates = (selectedFinancialYear, month) => {
  return {
    fromDate: moment
      .utc(moment(selectedFinancialYear?.toDate).month(month).startOf('month'))
      .format(),
    toDate: moment.utc(moment(selectedFinancialYear?.toDate).month(month).endOf('month')).format(),
  };
};

const startAndEndDateOfMonth = (month) => {
  return {
    fromDate: moment.utc(moment().month(month).startOf('month')).format(),
    toDate: moment.utc(moment().month(month).endOf('month')).format(),
  };
};

const removeFieldFromUrlParam = (key, navigate) => {
  const currentUrlParams = new URLSearchParams(window.location.search);
  currentUrlParams.delete(key);
  navigate(
    {
      pathname: window.location.pathname,
      search: currentUrlParams.toString(),
    },
    { replace: true }
  );
};

const setObjectToUrlParam = (paramsObj, navigate) => {
  const currentUrlParams = new URLSearchParams(window.location.search);
  Object.entries(paramsObj).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      currentUrlParams.set(key, value);
    } else {
      currentUrlParams.delete(key);
    }
  });

  navigate(
    {
      pathname: window.location.pathname,
      search: currentUrlParams.toString(),
    },
    { replace: true }
  );
};

const setFilterToUrl = (obj, navigate) => {
  if (obj && Object.keys(obj).length) {
    const currentUrlParams = new URLSearchParams();
    Object.entries(obj).forEach(([key, value]) => {
      if (value == null) {
        currentUrlParams.delete(key);
      } else {
        currentUrlParams.set(key, value);
      }
    });
    navigate(
      {
        pathname: window.location.pathname,
        search: currentUrlParams.toString(),
      },
      { replace: true }
    );
  }
};

const reloadParentDelivery = () => {
  publish(APP_EVENTS.topic.DELIVERY, APP_EVENTS.event.CHANGE_DELIVERY, null);
};

function deepKeyBy(arr, key = 'id') {
  return keyBy(
    map(arr, (o) => ({ ...o, districts: keyBy(o.districts, key) })),
    key
  );
}

function hasError(response) {
  if (response?.status >= 400) {
    return errorExtractor(response);
  }
  return false;
}

function isSpecialCharacter(character) {
  const specialCharacterRegex = /[^A-Za-z0-9 ]/g;
  return specialCharacterRegex.test(character);
}

function isEquals(from, to) {
  return from.replace(/\s+/g, '').toLowerCase() === to.replace(/\s+/g, '').toLowerCase();
}

function percentage(percent, total) {
  return Math.round((percent / 100) * total || 0);
}

function getSearchParams(param) {
  const params = new URLSearchParams(window.location.search);
  return params.get(param);
}

export const getArialDistance = (p1, p2) => {
  const R = 6378137; // Earth’s mean radius in meters
  const rad = (x) => (x * Math.PI) / 180;
  const dLat = rad(p2.lat - p1.lat);
  const dLong = rad(p2.lng - p1.lng);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(p1.lat)) * Math.cos(rad(p2.lat)) * Math.sin(dLong / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  const dInKm = d / 1000;
  return Math.ceil(dInKm + percentage(18, dInKm));
};

export const getHSNCode = (browseNodes) => {
  const hsnCodeArray = [];
  for (const item of browseNodes?.data || []) {
    for (const itemHSN of item?.items || []) {
      hsnCodeArray.push({
        id: Number(itemHSN?.hsnCode?.id),
        tax: 5,
      });
    }
  }
  return uniqBy(hsnCodeArray, 'id');
};

export const getTaxableValue = (qty, pricePerUnit) => qty * pricePerUnit;

export const getGstRate = (materials, itemId) =>
  materials?.find((material) => material?.id === itemId)?.hsnCode?.tax;

export const getHsnCode = (materials, itemId) =>
  materials?.find((material) => material?.id === itemId)?.hsnCode?.id;

export const getGstAmount = (taxableValue, gstRate) => taxableValue * (gstRate / 100);

export const getLineAmount = (taxableValue, gstAmount) => taxableValue + gstAmount;

export const convertToMt = (value, uptoDigit = 2) =>
  value ? parseFloat((value / 1000).toFixed(uptoDigit)) : 0;

export const browseNodesToCategory = (categoryBrowseNodeId) => {
  switch (categoryBrowseNodeId) {
    case '1':
      return '1';
    case '2':
      return '3';
    case '3':
      return 'E-waste';
    case '4':
      return '2';
    case '5':
      return 'Batteries';
    case '6':
      return '4';
    case '7':
      return 'Tyres';
    default:
      return 'error parsing browse node';
  }
};

export const checkAmountType = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '0';
  if (value === 0) return '0';
  const length = value.toFixed().length;
  if (length <= 5) return `${getDecimalValue(value / 1000, 3)} K`;
  if (length <= 7) return `${getDecimalValue(value / 100000)} Lac`;
  return `${getDecimalValue(value / 10000000)} Cr`;
};
export const formatCurrencyAmount = (value) => {
  if (!value) return { value: '', postfix: '' };

  const absValue = Math.abs(value);
  const length = absValue?.toFixed()?.length;

  if (length <= 5) {
    return {
      value: getDecimalValue(value / 1000, 3),
      postfix: 'K',
    };
  }

  if (length <= 7) {
    return {
      value: getDecimalValue(value / 100000),
      postfix: 'Lac',
    };
  }

  return {
    value: getDecimalValue(value / 10000000),
    postfix: 'Cr',
  };
};
export function findPercentage(planned, total) {
  return Math.round((planned * 100) / total || 0);
}

export function findDeliverables(deliverablesArray, customerId) {
  const deliverablesArrayByCustomerId = deliverablesArray[customerId] || [];
  const specialDeliverablesArray = deliverablesArrayByCustomerId.filter((data) => data?.special);

  return {
    deliverablesArrayByCustomerId,
    specialDeliverablesArray,
    docType: specialDeliverablesArray.map((data) => data?.docType),
    isSpecial: specialDeliverablesArray.length > 0,
  };
}

export const convertToKG = (value) => value * 1000;

export const convertToMT = (value) => value / 1000;

export const convertToLakhs = (value) => value / 100000;

export const convertToMTwithPrecision = (value, precision = 6) => {
  if (value === undefined || value === null) return 0;
  return parseFloat((value / 1000).toFixed(precision));
};

export const toDecimals = (value, precision = 3) => {
  return Number.isInteger(value) ? value : parseFloat(value.toFixed(precision));
};

export const convertToRupee = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumSignificantDigits: 3,
  }).format(+value);

function convertUTCtoIST(utcDate) {
  const date = new Date(utcDate);
  date.setMinutes(date.getMinutes() + 330); // IST is UTC+5:30
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function isValidDateFormat(dateStr) {
  const regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}([+-]\d{2}:\d{2}|Z)$/;
  return regex.test(dateStr);
}
const getHashParam = () => {
  const hash = window.location.hash;
  return hash ? hash.substring(1) : null;
};
function browserImageSize(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

const HtmlRenderer = ({ html, as: Tag = 'div', ...props }) => {
  return <Tag dangerouslySetInnerHTML={{ __html: html }} {...props} />;
};
const extractBodyFromHTML = (htmlString) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  return doc.body.innerHTML;
};

/**
 * Safely parses JSON strings with error handling
 * @param {string} jsonString - The JSON string to parse
 * @param {any} defaultValue - Value to return if parsing fails
 * @returns {any} Parsed object or default value
 */

/**
 * Safely stringifies values to JSON
 * @param {any} value - Value to stringify
 * @param {number} space - Number of spaces for indentation
 * @returns {string} JSON string or empty string on error
 */
export const safeJsonStringify = (value, space = 0) => {
  try {
    if (value === undefined || value === null) {
      return '';
    }
    return JSON.stringify(value, null, space);
  } catch (error) {
    console.warn(`JSON Stringify Error: ${error.message}`);
    return '';
  }
};

/**
 * Safely parses JSON strings with error handling
 * @param {string} jsonString - The JSON string to parse
 * @param {any} defaultValue - Value to return if parsing fails
 * @returns {any} Parsed object or default value
 */
export const safeJsonParse = (jsonString, defaultValue = null) => {
  try {
    if (jsonString === undefined || jsonString === null) {
      return defaultValue;
    }

    // Handle non-string inputs
    if (typeof jsonString !== 'string') {
      return jsonString;
    }

    // Handle empty strings
    if (jsonString.trim() === '') {
      return defaultValue;
    }

    const parsed = JSON.parse(jsonString);
    return parsed;
  } catch (error) {
    console.warn(`JSON Parse Error: ${error.message}`);
    return defaultValue;
  }
};

/**
 * Safely stringifies values to JSON
 * @param {any} value - Value to stringify
 * @param {number} space - Number of spaces for indentation
 * @returns {string} JSON string or empty string on error
 */

const buildUrlWithParams = (pathname, params = {}) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value != null) {
      searchParams.set(key, value);
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `${pathname}?${queryString}` : pathname;
};

export const buildFilterParams = (filterKeysForQueryString, params = {}, filters = {}) => {
  const newFilterEntries = filterKeysForQueryString
    .filter((key) => params[key] !== undefined && params[key] !== '')
    .map((key) => `${key}=${params[key]}`);

  const existingFilterString = filters?.filters ? String(filters.filters).trim() : '';

  const mergedFilterString = [existingFilterString, ...newFilterEntries]
    .filter(Boolean)
    .join(':::');

  const remainingParams = Object.keys(params).reduce((acc, key) => {
    if (!filterKeysForQueryString.includes(key)) {
      acc[key] = params[key];
    }
    return acc;
  }, {});

  return {
    filters: mergedFilterString || undefined,
    remainingParams,
  };
};

export const convertBytesToKB = (bytes) => {
  return (bytes / 1024).toFixed(2);
};

export {
  HtmlRenderer,
  browserImageSize,
  buildQueryString,
  buildUrlWithParams,
  convertUTCtoIST,
  deepKeyBy,
  extractBodyFromHTML,
  getColorByDocType,
  getCurrentYearStartEndDates,
  getDocumentByType,
  getHashParam,
  getNextYearStartEndDates,
  getSearchParams,
  groupBy,
  hasError,
  isEquals,
  isSpecialCharacter,
  isValidDateFormat,
  numberWithCommas,
  parseQueryParams,
  reloadParentDelivery,
  removeFieldFromUrlParam,
  setFilterToUrl,
  setObjectToUrlParam,
  startAndEndDateOfMonth,
  toCapitalize,
  toLocalDate,
  toLocalDateTime,
  toLocalDisplayDate,
  toServerDate,
  toServerFromAndToDate,
};
